// -----------------------------------------------------------------------------
// Location: packages/@honeycomb/ui-core/src/components/ActionButton/ActionButton.styles.ts
// -----------------------------------------------------------------------------

import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
    ActionButtonColor,
    ActionButtonElementColors,
    ActionButtonSize,
    ActionButtonTheme,
    ActionButtonVariant,
    createResponsiveStyles,
    HoneycombTheme,
    mergeResponsiveStyleValues,
    ResponsiveStyleValue,
    useHoneycombTheme,
} from '../../theming';
import { alpha, isActionButtonThemeableColor } from '../../utils';

/* ----------------------------------
 * Helpers
 * ---------------------------------- */

const getElementColors = (color: ActionButtonColor, componentTheme: ActionButtonTheme): ActionButtonElementColors => {
    if (isActionButtonThemeableColor(color)) {
        return componentTheme.colors[color];
    }
    return componentTheme.fixedColors[color];
};

const getColorVariables = (colors: ActionButtonElementColors, theme: HoneycombTheme): CSSObject => {
    const { hoverBackground, content } = theme.fn.resolveFlatColorObject(colors);
    const componentTheme = theme.components.themes.ActionButton;

    return {
        '--action-button-color': content,
        '--action-button-hover-color': alpha(hoverBackground, componentTheme.hoverOpacity),
    };
};

function getVisibilityStyles(values: { hideLabel: boolean; hideIcon: boolean }): CSSObject {
    const { hideLabel, hideIcon } = values;

    return {
        '[data-label]': {
            display: hideLabel ? 'none' : 'block',
        },
        '[data-icon]': {
            display: hideIcon ? 'none' : 'flex',
        },
    };
}

/**
 * Variant owns structural intent (height, shape).
 */
function getVariantVariables(variant: ActionButtonVariant): CSSObject {
    if (variant === 'stretched') {
        return {
            '--action-button-height': '100%',
            '--action-button-border-radius': '0px',
        };
    }

    return {};
}

/**
 * Size owns density only (icon, gap, typography).
 * It must never override height.
 */
function getSizeVariables(size: ActionButtonSize): CSSObject {
    if (size === 's') {
        return {
            '--action-button-icon-size': '20px',
            '--action-button-gap': `calc(var(--hc-spacing-unit) * 1.5)`,
            '--action-button-font-size': '0.875rem',
            '--action-button-padding-x': 'calc(var(--hc-spacing-unit) * 3)',
        };
    }

    return {
        '--action-button-font-size': '1rem',
    };
}

/* ----------------------------------
 * Static styles
 * ---------------------------------- */

export const STATIC_STYLES: SerializedStyles = css`
    --action-button-height: 40px;
    --action-button-icon-size: 24px;
    --action-button-gap: calc(var(--hc-spacing-unit) * 2);
    --action-button-padding-x: calc(var(--hc-spacing-unit) * 3);
    --action-button-edge-adjustment: calc(var(--action-button-padding-x) * -1);

    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--action-button-gap);

    height: var(--action-button-height);
    padding: 0 var(--action-button-padding-x);
    border-radius: var(--action-button-border-radius);

    color: var(--action-button-color);

    &:hover,
    &:focus-visible {
        background-color: var(--action-button-hover-color);
    }

    &[data-full-width] {
        display: flex;
        width: 100%;
    }

    [data-icon] {
        display: flex;
        gap: calc(var(--hc-spacing-unit) * 1);
        height: var(--action-button-icon-size);

        svg {
            width: var(--action-button-icon-size);
            height: var(--action-button-icon-size);
        }
    }

    [data-label] {
        font-size: var(--action-button-font-size);
    }

    &[data-align-edge-start] {
        margin-inline-start: var(--action-button-edge-adjustment);
    }

    &[data-align-edge-end] {
        margin-inline-end: var(--action-button-edge-adjustment);
    }
`;

/* ----------------------------------
 * Dynamic styles hook
 * ---------------------------------- */

export function useDynamicStyles(
    color: ActionButtonColor,
    hideLabel: ResponsiveStyleValue<boolean>,
    hideIcon: ResponsiveStyleValue<boolean>,
    size: ResponsiveStyleValue<ActionButtonSize>,
    variant: ActionButtonVariant
): CSSObject {
    const { theme } = useHoneycombTheme();
    const componentTheme = theme.components.themes.ActionButton;

    const hasResponsiveStyles = hideLabel || hideIcon || size;

    return {
        '--action-button-border-radius': `${componentTheme.borderRadius}px`,
        ...getVariantVariables(variant),
        ...getColorVariables(getElementColors(color, componentTheme), theme),
        ...(hasResponsiveStyles
            ? createResponsiveStyles(
                  [
                      {
                          value: mergeResponsiveStyleValues({ hideLabel, hideIcon, size }, theme),
                          getStyles: (values) => ({
                              ...getVisibilityStyles(values),
                              ...(values.size ? getSizeVariables(values.size) : {}),
                          }),
                      },
                  ],
                  theme
              )
            : {}),
    };
}
